import { AfterContentInit, AfterViewInit, Component, ContentChildren, Input, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { ScrollPanel } from 'primeng/scrollpanel';
import { LayoutTemplateDirective } from './layout-template.directive';

export enum MenuOrientation {
  STATIC,
  OVERLAY,
  SLIM,
  HORIZONTAL
}

@Component({
  selector: 'app-layout',
  template: `
    <div class="layout-wrapper menu-layout-static"
      [ngClass]="{
        'menu-layout-static': !isOverlay(),
        'menu-layout-overlay': isOverlay(),
        'layout-menu-overlay-active': overlayMenuActive,
        'menu-layout-horizontal': isHorizontal(),
        'menu-layout-slim': isSlim(),
        'layout-menu-static-inactive': staticMenuDesktopInactive,
        'layout-menu-static-active': staticMenuMobileActive}"
      (click)="onLayoutClick()"
      >
      <div class="layout-header" [style.display]="showHeader ? 'block' : 'none'">
        <app-topbar
          *ifLogged
          [slimMenu]="isSlim()"
          [userFullname]="userFullname"
          [userLogin]="userLogin"
          [profileMenuTemplate]="profileMenuTemplate"
          [items]="topbarItemsTemplate"
          [logo]="logoTemplate"
          (visibilityChange)="onMenuVisibilityChange($event)"
        ></app-topbar>
        <div class="layout-menu-container" *ifLogged (click)="onMenuClick()">
            <div class="menu-scroll-content">
              <app-inline-profile *ngIf="profileMode==='inline' && !isHorizontal()"
                [userFullname]="userFullname"
                [userLogin]="userLogin"
              ></app-inline-profile>
              <app-menu [reset]="resetMenu"></app-menu>
            </div>
        </div>
      </div>
      <div class="layout-main">
        <p-toast></p-toast>
        <ng-container *ngIf="showBreadcrumb">
          <app-breadcrumb *ifLogged></app-breadcrumb>
        </ng-container>
        <ng-content></ng-content>
      </div>
    </div>
    <app-loader></app-loader>
  `,
  styles: [`
  :host ::ng-deep .ui-scrollpanel-content {
    width: 100%;
  }
  .layout-header {
    height: 60px;
  }
  .layout-main {
    overflow: auto;
    min-height: calc(100vh - 60px);
    padding-top: 0 !important;
    height: calc(100vh - 60px);
  }
  @media print {
    .layout-header {
      display: none !important;
    }

    .layout-main {
      padding-top: 5px;
      height: auto;
    }
  }
`]
})
export class LayoutComponent implements AfterViewInit, AfterContentInit {
  @Input() layoutMode: MenuOrientation = MenuOrientation.HORIZONTAL;
  @Input() profileMode = 'top';
  @Input() showHeader = true;
  @Input() userFullname: string | undefined;
  @Input() userLogin: string | undefined;
  @Input() showBreadcrumb = true;
  @ViewChild('layoutMenuScroller', { static: false }) layoutMenuScrollerViewChild!: ScrollPanel;
  @ContentChildren(LayoutTemplateDirective) templates!: QueryList<LayoutTemplateDirective>;

  public rotateMenuButton = false;
  public topbarMenuActive = false;
  public overlayMenuActive = false;
  public staticMenuDesktopInactive = false;
  public staticMenuMobileActive = false;
  public menuClick = false;
  public topbarItemClick = false;
  public activeTopbarItem: unknown;
  public resetMenu = false;
  public menuHoverActive = false;
  public profileMenuTemplate!: TemplateRef<unknown>;
  public topbarItemsTemplate!: TemplateRef<unknown>;
  public logoTemplate!: TemplateRef<unknown>;

  ngAfterViewInit(): void {
    setTimeout(() => { this.moveBar(); }, 100);
  }

  ngAfterContentInit(): void {
    this.templates.forEach(t => {
      switch (t.getType()) {
        case 'profileMenu':
          this.profileMenuTemplate = t.template;
          break;
        case 'topbarItems':
          this.topbarItemsTemplate = t.template;
          break;
        case 'logo':
          this.logoTemplate = t.template;
          break;
      }
    });
  }

  onMenuVisibilityChange(close: boolean): void {
    if (close && this.layoutMode === MenuOrientation.STATIC) {
      this.layoutMode = MenuOrientation.SLIM;
    } else if (!close && this.layoutMode === MenuOrientation.SLIM) {
      this.layoutMode = MenuOrientation.STATIC;
    } else {
      this.onMenuButtonClick();
    }
  }

  onLayoutClick(): void {
    if (!this.topbarItemClick) {
      this.activeTopbarItem = null;
      this.topbarMenuActive = false;
    }

    if (!this.menuClick) {
      if (this.isHorizontal() || this.isSlim()) {
        this.resetMenu = true;
      }

      if (this.overlayMenuActive || this.staticMenuMobileActive) {
        this.hideOverlayMenu();
      }

      this.menuHoverActive = false;
    }

    this.topbarItemClick = false;
    this.menuClick = false;
  }

  onMenuButtonClick(event?: MouseEvent): void {
    this.menuClick = true;
    this.rotateMenuButton = !this.rotateMenuButton;
    this.topbarMenuActive = false;

    if (this.layoutMode === MenuOrientation.OVERLAY) {
      this.overlayMenuActive = !this.overlayMenuActive;
    } else {
      if (this.isDesktop()) {
        this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
      } else {
        this.staticMenuMobileActive = !this.staticMenuMobileActive;
      }
    }

    if (event) {
      event.preventDefault();
    }
  }

  onMenuClick(): void {
    this.menuClick = true;
    this.resetMenu = false;

    if (!this.isHorizontal()) {
      setTimeout(() => { this.moveBar(); }, 450);
    }
  }

  onTopbarMenuButtonClick(event: MouseEvent): void {
    this.topbarItemClick = true;
    this.topbarMenuActive = !this.topbarMenuActive;

    this.hideOverlayMenu();

    event.preventDefault();
  }

  onTopbarItemClick(event: MouseEvent, item: unknown): void {
    this.topbarItemClick = true;

    if (this.activeTopbarItem === item) {
      this.activeTopbarItem = null;
    } else {
      this.activeTopbarItem = item;
    }

    event.preventDefault();
  }

  onTopbarSubItemClick(event: MouseEvent): void {
    event.preventDefault();
  }

  hideOverlayMenu(): void {
    this.rotateMenuButton = false;
    this.overlayMenuActive = false;
    this.staticMenuMobileActive = false;
  }

  isTablet(): boolean {
    const width = window.innerWidth;
    return width <= 1024 && width > 640;
  }

  isDesktop(): boolean {
    return window.innerWidth > 1024;
  }

  isMobile(): boolean {
    return window.innerWidth <= 640;
  }

  isOverlay(): boolean {
    return this.layoutMode === MenuOrientation.OVERLAY;
  }

  isHorizontal(): boolean {
    return this.layoutMode === MenuOrientation.HORIZONTAL;
  }

  isSlim(): boolean {
    return this.layoutMode === MenuOrientation.SLIM;
  }

  changeToStaticMenu(): void {
    this.layoutMode = MenuOrientation.STATIC;
  }

  changeToOverlayMenu(): void {
    this.layoutMode = MenuOrientation.OVERLAY;
  }

  changeToHorizontalMenu(): void {
    this.layoutMode = MenuOrientation.HORIZONTAL;
  }

  changeToSlimMenu(): void {
    this.layoutMode = MenuOrientation.SLIM;
  }

  private moveBar(): void {
    if (this.layoutMenuScrollerViewChild) {
      this.layoutMenuScrollerViewChild.moveBar();
    }
  }
}
